import { isDefined } from "@clipboard-health/util-ts";
import { type TimeSlotWithExtras } from "@src/appV2/Shifts/types";

import { type QualificationSpecificOverrides, type ShiftTimeDefinition } from "./useGetShiftTimes";

export interface ShiftTimeData {
  start: { hour: number; minute: number };
  duration: { hours: number; minutes: number };
}

export interface ShiftTimesCachedData {
  standardDefs: Partial<Record<"am" | "pm" | "noc", ShiftTimeDefinition>> | undefined;
  qualificationSpecific: QualificationSpecificOverrides | undefined;
  extraDefs?: Array<ShiftTimeDefinition & { name: string }>;
}

function applyOptimisticOrientationUpdate(
  existingData: ShiftTimesCachedData,
  shiftTimeData: ShiftTimeData
): ShiftTimesCachedData {
  const existingExtraDefs = existingData.extraDefs ?? [];
  const nextExtraDefs = existingExtraDefs.some((entry) => entry.name === "ori")
    ? existingExtraDefs.map((entry) =>
        entry.name === "ori" ? { ...entry, ...shiftTimeData } : entry
      )
    : [...existingExtraDefs, { name: "ori", ...shiftTimeData }];
  return { ...existingData, extraDefs: nextExtraDefs };
}

/**
 * Computes the next cached `shiftTimes` query data after a default-time update.
 *
 * - `"ori"` updates the workplace-wide `extraDefs[name="ori"]` entry.
 * - With `qualification`: writes a per-qualification override.
 * - Without `qualification` (apply to all): updates `standardDefs[timeSlot]`
 *   and rewrites every qualification override that already had a value for
 *   this slot so it stays in sync.
 */
export function applyOptimisticUpdate(
  existingData: ShiftTimesCachedData | undefined,
  timeSlot: TimeSlotWithExtras,
  shiftTimeData: ShiftTimeData,
  qualification: string | undefined
): ShiftTimesCachedData | undefined {
  if (!existingData) {
    return existingData;
  }
  if (timeSlot === "ori") {
    return applyOptimisticOrientationUpdate(existingData, shiftTimeData);
  }
  if (isDefined(qualification)) {
    return {
      ...existingData,
      qualificationSpecific: {
        ...existingData.qualificationSpecific,
        [qualification]: {
          ...existingData.qualificationSpecific?.[qualification],
          [timeSlot]: shiftTimeData,
        },
      },
    };
  }
  let updatedQualSpecific = existingData.qualificationSpecific;
  if (isDefined(updatedQualSpecific)) {
    updatedQualSpecific = { ...updatedQualSpecific };
    for (const qual of Object.keys(updatedQualSpecific)) {
      if (isDefined(updatedQualSpecific[qual]?.[timeSlot])) {
        updatedQualSpecific[qual] = { ...updatedQualSpecific[qual], [timeSlot]: shiftTimeData };
      }
    }
  }
  return {
    ...existingData,
    standardDefs: { ...existingData.standardDefs, [timeSlot]: shiftTimeData },
    qualificationSpecific: updatedQualSpecific,
  };
}
